import React, { useEffect } from 'react'
import { Message } from 'intl'
import { constants, formatPrice } from 'helpers'
import { useAb } from 'hooks'
import { preloadModal } from 'modals'

import { OfferLabel, Text } from 'components/dataDisplay'
import { Explanation } from 'components/feedback'
import { buttonMessages } from 'components/inputs'
import { Bone } from 'components/layout'

import emptyProductImage from 'components/dataDisplay/Icon/images/other/product-with-plus.svg'

import OrderItemBase from 'compositions/orderItems/OrderItemBase/OrderItemBase'
import OrderProductBase from 'compositions/orderItems/OrderProductBase/OrderProductBase'
import OrderItemRemoveButton from 'compositions/orderItems/OrderItemRemoveButton/OrderItemRemoveButton'

import { useFirstProducts } from '../util'

import messages from './messages'


type SubscriptionOrderItemProps = {
  className?: string
  data: OrderItems.Subscription
  withRemoveButton?: boolean
  isThankYouPage?: boolean
}

const SubscriptionOrderItem: React.FunctionComponent<SubscriptionOrderItemProps> = (props) => {
  const { className, data, withRemoveButton, isThankYouPage } = props
  const { deliveryDate, freeProduct, plan, price, subscriptionPromos } = data

  const isUpchargeInfoPaymentEnabled = useAb(constants.abTests.upchargeInfoPayment) === 'b'

  const { firstProducts, isFetching, isSubmitting } = useFirstProducts({ data, isThankYouPage })
  const hasFirstProducts = firstProducts?.length > 0

  useEffect(() => {
    if (hasFirstProducts) {
      preloadModal('firstProductModal')
    }
  }, [ hasFirstProducts ])

  if (isFetching) {
    return (
      <Bone pw={100} h={100} bgColor="light-beige" />
    )
  }

  const productsPerPeriod = plan?.productsPerPeriod || 1
  const isSeveralProductsPlan = productsPerPeriod !== 1

  const caseMessage = hasFirstProducts
    ? messages.case
    : isSeveralProductsPlan ? { ...messages.emptyProductMultiPlan, values: { productsPerPeriod } } : messages.emptyProduct

  const promoNodes = subscriptionPromos?.map((subscriptionPromo) => {
    const { title, promo, amount } = subscriptionPromo
    const { id, image, rebrandImage, size } = promo

    return (
      <OrderProductBase
        key={`promo-${id}`}
        className="mt-24"
        price={null}
        name={title}
        image={image}
        rebrandImage={rebrandImage}
        quantity={amount}
        size={size}
        data-testid="subscriptionPromo"
      />
    )
  })

  const caseNode = (
    <OrderProductBase
      key="case"
      className="mt-24"
      price={hasFirstProducts ? null : price}
      data-testid="subscriptionCase"
    >
      <Text
        message={caseMessage}
        style="p2"
      />
    </OrderProductBase>
  )

  const freeProductNode = freeProduct ? (
    <OrderProductBase
      key="free-product"
      className="mt-24"
      image={freeProduct.image}
      rebrandImage={freeProduct.rebrandImage}
      name={freeProduct.name}
      brand={freeProduct.brand}
      size={freeProduct.size}
      price={null}
      data-testid="freeProduct"
    />
  ) : null

  const chosenProductNodes = hasFirstProducts ? firstProducts.map(({ flatIndex, product, isEmpty, onEdit }) => {
    const hasUpcharge = isUpchargeInfoPaymentEnabled && Boolean(product?.upchargePrice)

    const upchargePrice = hasUpcharge && formatPrice(product.upchargePrice, { removeZeroCents: true })

    return (
      <OrderProductBase
        key={flatIndex}
        className="mt-24"
        image={product?.image}
        rebrandImage={isEmpty ? emptyProductImage : product.rebrandImage}
        priceNode={onEdit ? (
          <button type="button" className="text-p2 underline cursor-pointer" onClick={onEdit} data-testid="editProduct">
            <Message value={messages.edit} />
          </button>
        ) : (
          <div className="pl-24" />
        )}
        data-testid={isEmpty ? 'firstProductEmpty' : 'firstProduct'}
      >

        {
          isEmpty ? (
            <>
              <Text style="p3" message={{ ...messages.productEmptyCell.title, values: { flatIndex } }} />
              <Text style="p5" color="gold-70" message={messages.productEmptyCell.note} />
            </>
          ) : (
            <>
              <Text className="mb-8" style="h8" color="gold-50" message={product.brand} />
              <Text style="p3" message={product.name} />
              {
                hasUpcharge && (
                  <div className="mt-8 flex justify-start items-center">
                    <OfferLabel title={{ ...messages.upcharge.label, values: { price: upchargePrice } }} />
                    <Explanation title={messages.upcharge.explanation.title} text={messages.upcharge.explanation.text}>
                      <Text className="ml-8 py-4 underline" message={buttonMessages.whatsThis} style="p5" />
                    </Explanation>
                  </div>
                )
              }
              {
                !isSeveralProductsPlan && (
                  <Text className="mt-8" style="p5" color="gold-50" message={messages.firstFragrance} />
                )
              }
            </>
          )
        }
      </OrderProductBase>
    )
  }) : null

  return (
    <OrderItemBase className={className} data-testid="orderSubscription">
      {
        withRemoveButton && (
          <OrderItemRemoveButton
            input={{
              subscriptionItem: {
                plan: plan.name,
              },
            }}
            metadata={{
              productType: 'Fragrance subscription',
              placement: 'Drawer cart',
            }}
          />
        )
      }
      <Text style="h9" message={messages.title} color="black" />
      <div className="mt-16 flex items-center">
        <Text
          message={messages.text}
          style="p2"
          color="black"
        />
        {
          hasFirstProducts && (
            <Text
              className="ml-24"
              message={price}
              style="t2"
              data-testid="price"
            />
          )
        }
      </div>
      <Text
        className="mt-24"
        message={{ ...messages.arrives, values: { month: deliveryDate.format('MMMM') } }}
        style="h9"
        color="black"
        data-testid="delivery"
      />
      {
        isSeveralProductsPlan && hasFirstProducts && (
          <Text className="mt-8" message={messages.firstMultiFragrances} style="p5" color="gold-70" />
        )
      }
      {
        isSubmitting ? (
          firstProducts?.map(({ flatIndex }) => (
            <Bone
              key={flatIndex}
              className="mt-24"
              pw={100}
              h={100}
              bgColor="light-beige"
            />
          ))
        ) : (
          chosenProductNodes
        )
      }
      {promoNodes}
      {caseNode}
      {freeProductNode}
    </OrderItemBase>
  )
}


export default SubscriptionOrderItem
